import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import type { Money } from "../api/types";
import {
  makeMoney,
  isZero,
  isNegative,
  lessThan,
  greaterThan,
} from "../logic/money";

interface SplitTransactionDialogProps {
  open: boolean;
  onClose: () => void;
  onSplit: (amount: Money) => void;
  originalAmount: Money;
}

export const SplitTransactionDialog = ({
  open,
  onClose,
  onSplit,
  originalAmount,
}: SplitTransactionDialogProps) => {
  const [amount, setAmount] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const handleSplit = () => {
    if (amount.trim() === "" || isNaN(parseFloat(amount))) {
      setError("Invalid amount");
      return;
    }

    const val = makeMoney(amount, originalAmount.currency);

    if (!isNegative(originalAmount)) {
      if (isZero(val) || isNegative(val)) {
        setError("Amount must be positive");
        return;
      }
      if (!lessThan(val, originalAmount)) {
        setError("Amount must be less than original amount");
        return;
      }
    } else {
      if (!isNegative(val)) {
        setError("Amount must be negative");
        return;
      }
      if (!greaterThan(val, originalAmount)) {
        setError("Amount must be greater than original amount");
        return;
      }
    }

    onSplit(val);
    onClose();
    setAmount("");
    setError(null);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Split Transaction</DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          Enter the amount for the new transaction. The original transaction
          amount will be reduced by this value.
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          label="New Amount"
          type="number"
          fullWidth
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            setError(null);
          }}
          error={!!error}
          helperText={error}
          slotProps={{ htmlInput: { step: "0.01" } }}
        />
        <Typography variant="caption" color="textSecondary">
          Original Amount: {originalAmount.amount}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSplit} color="primary" variant="contained">
          Split
        </Button>
      </DialogActions>
    </Dialog>
  );
};
